import { mat4, ReadonlyMat4 } from 'gl-matrix';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { DicomRigidRegistration } from '../dicom/dicomRigidRegistration';

export class RigidRegistration {
    public readonly transformation: mat4;
    public readonly sourceFrameOfReferenceUid: string;
    public readonly targetFrameOfReferenceUid: string;
    public readonly dicomParent: DicomRegistration;

    private _approvalStatus: DICOMApprovalStatus =
        DICOMApprovalStatus.UNAPPROVED;

    constructor(
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        transformation: mat4,
        parent: DicomRegistration
    ) {
        this.sourceFrameOfReferenceUid = sourceFrameOfReferenceUid;
        this.targetFrameOfReferenceUid = targetFrameOfReferenceUid;
        this.transformation = transformation;
        this.dicomParent = parent;
    }

    public get contentLabel(): string {
        return this.dicomParent.contentLabel;
    }

    public set contentLabel(label: string) {
        this.ensureCanModify();
        this.dicomParent.contentLabel = label;
    }

    public get contentDescription(): string {
        return this.dicomParent.contentDescription;
    }

    public set contentDescription(description: string) {
        this.ensureCanModify();
        this.dicomParent.contentDescription = description;
    }

    public get contentCreatorName(): string {
        return this.dicomParent.contentCreatorName;
    }

    public get contentDate(): string {
        return this.dicomParent.contentDate;
    }

    public get sopInstanceUid(): string {
        return this.dicomParent.sopInstanceUid;
    }

    public get dicomUid(): string {
        return this.sopInstanceUid;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomParent.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomParent.isNew;
    }

    public get isExported(): boolean {
        return this.dicomParent.isExported;
    }

    public setExported(): void {
        this.dicomParent.setExported();
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this._approvalStatus;
    }

    public get isApproved(): boolean {
        return this._approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public setApprovalStatus(status: DICOMApprovalStatus): void {
        this._approvalStatus = status;
    }

    public syncTransformationToDataset(): void {
        if (this.dicomParent instanceof DicomRigidRegistration) {
            this.dicomParent.syncTransformationToDataset();
        }
    }

    /**
     * Returns the transformation matrix that maps points FROM `fromFOR` space
     * INTO `intoFOR` space. Returns null if this registration doesn't link
     * the two frames of reference.
     *
     * @param fromFOR Frame of Reference UID of the coordinate space to transform FROM
     * @param intoFOR Frame of Reference UID of the coordinate space to transform INTO
     */
    public getTransformFromTo(
        fromFOR: string,
        intoFOR: string
    ): ReadonlyMat4 | undefined {
        if (fromFOR === intoFOR) {
            return mat4.create();
        }
        if (
            fromFOR === this.targetFrameOfReferenceUid &&
            intoFOR === this.sourceFrameOfReferenceUid
        ) {
            return this.transformation;
        }
        if (
            fromFOR === this.sourceFrameOfReferenceUid &&
            intoFOR === this.targetFrameOfReferenceUid
        ) {
            return mat4.invert(mat4.create(), this.transformation);
        }
        return undefined;
    }

    private ensureCanModify(): void {
        if (this._approvalStatus === DICOMApprovalStatus.APPROVED) {
            throw new Error('Cannot modify an approved registration');
        }
    }
}
